// src/pages/purchase-requisition/hooks/useOrderData.jsx
import { useState, useEffect } from "react";
import {
    fetchFlowConfigurations,
    fetchAdminJabatanId,
    fetchPrcnInvMaterialPackPHRoleId,
    fetchInvManagementFreshPineOtherRoleId,
    fetchCostAnalystFreshPineOtherRoleId,
    fetchDeveloperRoleId,
    fetchUomList,
    fetchOrders
} from "../services/orderService";

/**
 * Custom hook to fetch and manage order-related data, including flow configurations,
 * admin jabatan ID, specific role ID, UoM list, and filtered orders.
 * @param {object} user - The authenticated user object.
 * @param {object} userProfile - The user's profile data.
 * @returns {object} An object containing all fetched data and loading states.
 */
const useOrderData = (user, userProfile) => {
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [flowConfigurations, setFlowConfigurations] = useState([]);
    const [loadingFlows, setLoadingFlows] = useState(true);
    const [adminJabatanId, setAdminJabatanId] = useState(null);
    const [loadingAdminJabatanId, setLoadingAdminJabatanId] = useState(true);
    const [uomList, setUomList] = useState([]);
    const [loadingUom, setLoadingUom] = useState(true);
    const [prcnInvMaterialPackPHRoleId, setPrcnInvMaterialPackPHRoleId] = useState(null);
    const [invManagementFreshPineOtherRoleId, setInvManagementFreshPineOtherRoleId] = useState(null); // New state for the new role
    const [costAnalystFreshPineOtherRoleId, setCostAnalystFreshPineOtherRoleId] = useState(null); // TAMBAHKAN
    const [developerRoleId, setDeveloperRoleId] = useState(null);
    const [loadingRoles, setLoadingRoles] = useState(true); // This now covers both roles

    // Fetch flow configurations from Firebase
    useEffect(() => {
        setLoadingFlows(true);
        const unsubscribe = fetchFlowConfigurations(
            (data) => {
                setFlowConfigurations(data);
                setLoadingFlows(false);
            },
            (error) => {
                console.error("Error fetching flow configurations:", error);
                setLoadingFlows(false);
            }
        );
        return unsubscribe;
    }, []);

    // Fetch admin position ID and specific role IDs from Firebase
    useEffect(() => {
        setLoadingAdminJabatanId(true);
        setLoadingRoles(true);

        const unsubscribeJabatan = fetchAdminJabatanId(
            (id) => {
                setAdminJabatanId(id);
                setLoadingAdminJabatanId(false);
            },
            (error) => {
                console.error("Error fetching jabatan configurations:", error);
                setLoadingAdminJabatanId(false);
            }
        );

        const unsubscribePrcnInvRoles = fetchPrcnInvMaterialPackPHRoleId(
            (id) => {
                setPrcnInvMaterialPackPHRoleId(id);
                // We will set loadingRoles to false only after both role fetches are complete.
                // For now, we'll let the next unsubscribe handle it.
            },
            (error) => {
                console.error("Error fetching Prcn. & Inv. Material Pack PH role configurations:", error);
                // If one fails, we should still try to load others.
            }
        );

        const unsubscribeInvMgtRoles = fetchInvManagementFreshPineOtherRoleId( // Fetch the new role ID
            (id) => {
                setInvManagementFreshPineOtherRoleId(id);
                // Set loadingRoles to false once this and other role fetches are done.
                // This logic might need refinement if there are many async fetches.
                // For simplicity, we'll assume the last one sets loading to false.
                setLoadingRoles(false);
            },
            (error) => {
                console.error("Error fetching Inv Management FreshPine & Other role configurations:", error);
                setLoadingRoles(false); // Ensure loading is false even on error
            }
        );

        const unsubscribeCostAnalystRoles = fetchCostAnalystFreshPineOtherRoleId( // TAMBAHKAN
            (id) => {
                setCostAnalystFreshPineOtherRoleId(id);
            },
            (error) => {
                console.error("Error fetching Cost Analyst Fresh Pine & Othrs role:", error);
            }
        );

        const unsubscribeDeveloperRoles = fetchDeveloperRoleId( // TAMBAHKAN
            (id) => {
                setDeveloperRoleId(id);
                // Set loadingRoles to false setelah semua role selesai di-fetch
                setLoadingRoles(false);
            },
            (error) => {
                console.error("Error fetching Developer role:", error);
                setLoadingRoles(false);
            }
        );


        return () => {
            unsubscribeJabatan();
            unsubscribePrcnInvRoles();
            unsubscribeInvMgtRoles(); // Cleanup for the new role fetch
            unsubscribeCostAnalystRoles(); // TAMBAHKAN
            unsubscribeDeveloperRoles(); // TAMBAHKAN
        };
    }, []);

    // Fetch UoM list from Firebase
    useEffect(() => {
        setLoadingUom(true);
        const unsubscribe = fetchUomList(
            (data) => {
                setUomList(data);
                setLoadingUom(false);
            },
            (error) => {
                console.error("Error fetching UoM configurations:", error);
                setLoadingUom(false);
            }
        );
        return unsubscribe;
    }, []);

    // Fetch orders from Firebase and apply frontend filter
    useEffect(() => {
        if (!user || !userProfile || loadingFlows || loadingUom || loadingRoles) {
            setLoadingOrders(true);
            return;
        }

        setLoadingOrders(true);
        const unsubscribe = fetchOrders(
            (allOrdersData) => {
                // Frontend filter logic to ensure visibility based on current approval step
                const filteredOrders = allOrdersData.filter(order => {
                    const userRoles = userProfile.id_role ? userProfile.id_role.split(';') : [];

                    // Condition 1: User is the creator of the order
                    if (order.created_by_uid === user.uid) {
                        return true;
                    }

                    const flowConfig = flowConfigurations.find(f => f.id === order.flow_config_id);
                    if (!flowConfig || !flowConfig.approval_steps) {
                        return false; // Cannot determine visibility if flow configuration is missing
                    }

                    const approvalStepsArray = Object.values(flowConfig.approval_steps).sort((a, b) => a.order - b.order);

                    // Condition 2: User is the current approver for a pending order
                    if (order.status === "Pending Approval") {
                        const currentStep = approvalStepsArray[order.current_approval_step_index];
                        if (currentStep) {
                            const isUserCurrentApprover =
                                (userProfile.id_role && Array.isArray(currentStep.target_role_ids) && userRoles.some(userRole => currentStep.target_role_ids.includes(userRole)));
                            if (isUserCurrentApprover) {
                                return true;
                            }
                        }
                    }

                    // Condition 3: User has approved a previous step in this order's history
                    // This includes cases where the order has passed their step, or has been completed.
                    if (Array.isArray(order.approval_history)) {
                        const userHasApprovedBefore = order.approval_history.some(historyEntry =>
                            historyEntry.approver_uid === user.uid
                        );
                        if (userHasApprovedBefore) {
                            return true;
                        }
                    }

                    // Condition 4: User has "Prcn. & Inv. Material Pack PH" OR "Inv Management FreshPine & Other" role
                    // and order is "Pending: Final Verification" or "Approved"
                    const hasRelevantRoleForFinalVerificationOrApproved =
                        (prcnInvMaterialPackPHRoleId && userRoles.includes(prcnInvMaterialPackPHRoleId)) ||
                        (invManagementFreshPineOtherRoleId && userRoles.includes(invManagementFreshPineOtherRoleId));

                    if (hasRelevantRoleForFinalVerificationOrApproved) {
                        const finalVerificationStep = approvalStepsArray.find(step => step.step_name === "Verifikasi Akhir");
                        if (finalVerificationStep && order.status === "Pending Approval" && order.current_approval_step_index === (finalVerificationStep.order - 1)) {
                            return true;
                        }
                        // This role can also see "Approved" orders
                        if (order.status === "Approved") {
                            return true;
                        }
                    }

                    // If none of the above conditions are met, the user cannot see the order
                    return false;
                });

                setOrders(filteredOrders.reverse());
                setLoadingOrders(false);
            },
            (error) => {
                console.error("Error fetching orders:", error);
                setLoadingOrders(false);
            }
        );
        return unsubscribe;
    }, [user, userProfile, loadingFlows, flowConfigurations, loadingUom, loadingRoles, prcnInvMaterialPackPHRoleId, invManagementFreshPineOtherRoleId]);

    return {
        orders,
        loadingOrders,
        flowConfigurations,
        loadingFlows,
        adminJabatanId,
        loadingAdminJabatanId,
        uomList,
        loadingUom,
        prcnInvMaterialPackPHRoleId,
        invManagementFreshPineOtherRoleId, // Return the new role ID
        costAnalystFreshPineOtherRoleId, // TAMBAHKAN
        developerRoleId, // TAMBAHKAN
        loadingRoles,
    };
};

export default useOrderData;
